import React, { useEffect, useState } from 'react';
import { useSafeThemeClasses } from '@/shared/theme';
import { adminApiService } from '@/shared/services/adminApiService';
import { logger } from '@/shared/utils';

type SystemStats = {
  users: { total: number; active: number; inactive: number };
  blogs: { total: number; published: number; drafts: number };
  sessions: { active: number };
  system_health: { status: 'healthy' | 'warning' | 'degraded'; issues: string[]; timestamp: string };
};

type AdminSection = 'overview' | 'users' | 'blogs' | 'analytics' | 'portfolio' | 'errors' | 'activity';

interface AdminOverviewPageProps {
  onNavigate: (section: AdminSection) => void;
}

const healthColor = (theme: ReturnType<typeof useSafeThemeClasses>, status: SystemStats['system_health']['status']) => {
  if (status === 'healthy') return theme.alertSuccess;
  if (status === 'warning') return theme.alertWarning;
  return theme.alertError;
};

const AdminOverviewPage: React.FC<AdminOverviewPageProps> = ({ onNavigate }) => {
  const theme = useSafeThemeClasses();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApiService.getSystemStats();
        if (response.success && response.data) {
          setStats(response.data);
        } else {
          setError(response.error?.message || 'Failed to load system stats');
          logger.error('Failed to load system stats', 'AdminOverviewPage', { error: response.error });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load system stats');
        logger.error('Network error loading system stats', 'AdminOverviewPage', { error: err });
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const quickLinks: Array<{ section: AdminSection; label: string; description: string; icon: React.ReactNode }> = [
    {
      section: 'users',
      label: 'Users',
      description: 'Manage accounts, roles & access',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-4.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-4-4" />
        </svg>
      ),
    },
    {
      section: 'blogs',
      label: 'Blogs',
      description: 'Review, publish & moderate posts',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10l6 6v10a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      section: 'analytics',
      label: 'Analytics',
      description: 'Traffic, growth & engagement',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14" />
        </svg>
      ),
    },
    {
      section: 'portfolio',
      label: 'Portfolio',
      description: 'Manage showcased projects',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
    },
    {
      section: 'activity',
      label: 'Activity',
      description: 'Project-wide activity history',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      section: 'errors',
      label: 'Errors',
      description: 'Frontend & backend error reports',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className={`${theme.card} rounded-2xl p-12 text-center`}>
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.textAccent} mx-auto mb-4`}></div>
          <p className={theme.textSecondary}>Loading system overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h2 className={`text-2xl font-bold mb-1 ${theme.textHeading} tracking-tight`}>Admin Overview</h2>
        <p className={`${theme.textSecondary}`}>A snapshot of the platform, and quick links to everything you manage.</p>
      </div>

      {error && (
        <div className={`${theme.alertError} rounded-xl p-4`}>
          <p className={theme.textError}>{error}</p>
        </div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className={`rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${theme.card}`}>
              <h3 className={`text-sm font-medium mb-2 ${theme.textMuted} tracking-wide`}>Total Users</h3>
              <p className={`text-3xl font-bold mb-2 ${theme.textHeading} tracking-tight`}>{stats.users.total.toLocaleString()}</p>
              <p className={`text-sm ${theme.textSecondary}`}>{stats.users.active.toLocaleString()} active · {stats.users.inactive.toLocaleString()} inactive</p>
            </div>
            <div className={`rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${theme.card}`}>
              <h3 className={`text-sm font-medium mb-2 ${theme.textMuted} tracking-wide`}>Total Blogs</h3>
              <p className={`text-3xl font-bold mb-2 ${theme.textHeading} tracking-tight`}>{stats.blogs.total.toLocaleString()}</p>
              <p className={`text-sm ${theme.textSecondary}`}>{stats.blogs.published.toLocaleString()} published · {stats.blogs.drafts.toLocaleString()} drafts</p>
            </div>
            <div className={`rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${theme.card}`}>
              <h3 className={`text-sm font-medium mb-2 ${theme.textMuted} tracking-wide`}>Active Sessions</h3>
              <p className={`text-3xl font-bold mb-2 ${theme.textHeading} tracking-tight`}>{stats.sessions.active.toLocaleString()}</p>
              <p className={`text-sm ${theme.textSecondary}`}>Right now</p>
            </div>
            <div className={`rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${theme.card}`}>
              <h3 className={`text-sm font-medium mb-2 ${theme.textMuted} tracking-wide`}>System Health</h3>
              <p className={`text-lg font-bold mb-2 capitalize ${healthColor(theme, stats.system_health.status)}`}>{stats.system_health.status}</p>
              {stats.system_health.issues.length > 0 ? (
                <ul className={`text-sm space-y-0.5 ${theme.textSecondary}`}>
                  {stats.system_health.issues.slice(0, 3).map((issue, i) => (
                    <li key={i}>• {issue}</li>
                  ))}
                </ul>
              ) : (
                <p className={`text-sm ${theme.textSecondary}`}>No issues detected</p>
              )}
            </div>
          </div>
        </>
      )}

      <div>
        <h3 className={`text-lg font-semibold mb-3 ${theme.textHeading} tracking-tight`}>Manage</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickLinks.map((link) => (
            <button
              key={link.section}
              onClick={() => onNavigate(link.section)}
              className={`text-left rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${theme.card}`}
            >
              <div className={`mb-3 ${theme.textAccent}`}>{link.icon}</div>
              <h4 className={`font-semibold mb-1 ${theme.textHeading}`}>{link.label}</h4>
              <p className={`text-sm ${theme.textSecondary}`}>{link.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminOverviewPage;
